import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, FileDown, FileText, Loader2, Paperclip, Plus, Trash2, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { usePageMeta } from "@/components/site/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = ["Policy", "Form", "Circular", "Other"];
const DOC_EXT = ["pdf", "doc", "docx", "xls", "xlsx"];
const DOC_MAX = 10 * 1024 * 1024;

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
const fmtSize = (b) => (b >= 1024 * 1024 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

export default function PortalDocuments() {
  usePageMeta("Documents — SKN Group Portal", "Company document hub.");
  const { user } = useAuth();
  const isManager = user?.is_manager;

  const [docs, setDocs] = useState(null);
  const [filter, setFilter] = useState("All");
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("Other");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const fetchDocs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/documents`, { withCredentials: true });
      setDocs(res.data);
    } catch (err) {
      console.error("Failed to load documents", err);
      setDocs([]);
    }
  }, []);

  useEffect(() => {
    fetchDocs();
  }, [fetchDocs]);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const ext = f.name.includes(".") ? f.name.split(".").pop().toLowerCase() : "";
    if (!DOC_EXT.includes(ext)) {
      toast.error("File must be PDF, DOC/DOCX or XLS/XLSX.");
      e.target.value = "";
      return;
    }
    if (f.size > DOC_MAX) {
      toast.error("File must be smaller than 10MB.");
      e.target.value = "";
      return;
    }
    setFile(f);
  };

  const upload = async () => {
    if (!title.trim() || !file) {
      toast.error("Title and file are required.");
      return;
    }
    setUploading(true);
    try {
      const data = new FormData();
      data.append("title", title.trim());
      data.append("category", category);
      data.append("file", file);
      await axios.post(`${API}/documents`, data, { withCredentials: true });
      toast.success("Document uploaded.");
      setOpen(false);
      setTitle("");
      setCategory("Other");
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      fetchDocs();
    } catch (err) {
      console.error("Failed to upload document", err);
      toast.error("Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/documents/${id}`, { withCredentials: true });
      toast.success("Document deleted.");
      fetchDocs();
    } catch (err) {
      console.error("Failed to delete document", err);
      toast.error("Could not delete the document.");
    }
  };

  const visible = docs === null ? [] : filter === "All" ? docs : docs.filter((d) => d.category === filter);

  return (
    <div className="pt-32 md:pt-40 pb-24 min-h-screen" data-testid="portal-documents-page">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <Link to="/portal" data-testid="documents-back-link" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-[#B8997A] transition-colors">
          <ArrowLeft size={14} strokeWidth={1.5} /> Back to Portal
        </Link>
        <div className="mt-6 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tight">
            Document <span className="italic bronze">Hub</span>
          </h1>
          {isManager && (
            <button
              onClick={() => setOpen(true)}
              data-testid="document-upload-button"
              className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider border border-foreground/25 px-4 py-2.5 hover:border-[#B8997A] hover:text-[#B8997A] transition-colors self-start"
            >
              <Plus size={13} strokeWidth={1.5} /> Upload Document
            </button>
          )}
        </div>

        <div className="mt-8 flex flex-wrap gap-2" data-testid="documents-filters">
          {["All", ...CATEGORIES].map((c) => (
            <button
              key={c}
              data-testid={`documents-filter-${c.toLowerCase()}`}
              onClick={() => setFilter(c)}
              className={`px-4 py-1.5 text-xs uppercase tracking-wider border transition-colors duration-300 ${
                filter === c
                  ? "bg-foreground text-background border-foreground"
                  : "border-foreground/20 text-muted-foreground hover:border-[#B8997A] hover:text-[#B8997A]"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {docs === null ? (
          <div className="flex justify-center py-16">
            <Loader2 size={24} className="animate-spin bronze" />
          </div>
        ) : visible.length === 0 ? (
          <div className="mt-8 border border-foreground/15 p-10 text-center" data-testid="documents-empty">
            <FileText size={22} strokeWidth={1.25} className="bronze mx-auto" />
            <p className="mt-3 font-display text-lg font-medium">No documents{filter !== "All" ? ` in ${filter}` : " yet"}</p>
          </div>
        ) : (
          <div className="mt-8 space-y-px bg-border border border-foreground/10" data-testid="documents-list">
            {visible.map((d) => (
              <div key={d.id} className="bg-background p-5 flex items-center justify-between gap-4" data-testid={`document-item-${d.id}`}>
                <div className="flex items-center gap-4 min-w-0">
                  <FileText size={18} strokeWidth={1.25} className="bronze shrink-0" />
                  <div className="min-w-0">
                    <p className="font-medium text-sm md:text-base truncate">{d.title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5 truncate">
                      {d.filename} · {fmtSize(d.size)} · {d.uploaded_by} · {fmtDate(d.created_at)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <Badge variant="outline" className="rounded-none uppercase tracking-wider text-[10px] border-[#B8997A] text-[#8a6f52]">
                    {d.category}
                  </Badge>
                  <a
                    href={`${API}/documents/${d.id}/download`}
                    data-testid={`document-download-${d.id}`}
                    className="inline-flex items-center gap-1.5 text-sm bronze hover:underline"
                  >
                    <FileDown size={14} strokeWidth={1.5} /> Download
                  </a>
                  {isManager && (
                    <button
                      onClick={() => remove(d.id)}
                      data-testid={`document-delete-${d.id}`}
                      aria-label="Delete document"
                      className="p-2 border border-foreground/15 hover:border-destructive hover:text-destructive transition-colors"
                    >
                      <Trash2 size={12} strokeWidth={1.5} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="bg-[#F7F5F0] max-w-md" data-testid="document-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">Upload Document</DialogTitle>
            </DialogHeader>
            <div className="space-y-5 mt-2">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em]">Title</Label>
                <Input
                  data-testid="document-input-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g., Leave Policy 2026"
                  className="bg-transparent border-foreground/25"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em]">Category</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger data-testid="document-select-category" className="bg-transparent border-foreground/25">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#F7F5F0]">
                    {CATEGORIES.map((c) => (
                      <SelectItem key={c} value={c} data-testid={`document-category-${c.toLowerCase()}`}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em]">
                  File <span className="text-muted-foreground normal-case">(PDF/DOC/XLS, max 10MB)</span>
                </Label>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".pdf,.doc,.docx,.xls,.xlsx"
                  onChange={handleFile}
                  className="hidden"
                  id="doc-upload-input"
                  data-testid="document-input-file"
                />
                {file ? (
                  <div className="flex items-center gap-3 border border-[#B8997A]/50 px-4 py-3" data-testid="document-file-selected">
                    <FileText size={15} strokeWidth={1.5} className="bronze" />
                    <span className="text-sm flex-1 truncate">{file.name}</span>
                    <button
                      type="button"
                      onClick={() => {
                        setFile(null);
                        if (fileRef.current) fileRef.current.value = "";
                      }}
                      aria-label="Remove file"
                    >
                      <X size={15} strokeWidth={1.5} className="text-muted-foreground hover:text-destructive transition-colors" />
                    </button>
                  </div>
                ) : (
                  <label
                    htmlFor="doc-upload-input"
                    data-testid="document-file-trigger"
                    className="flex items-center gap-2 border border-dashed border-foreground/30 px-4 py-3 text-sm text-muted-foreground cursor-pointer hover:border-[#B8997A] hover:text-[#B8997A] transition-colors"
                  >
                    <Paperclip size={14} strokeWidth={1.5} /> Choose file
                  </label>
                )}
              </div>
              <button
                onClick={upload}
                disabled={uploading}
                data-testid="document-save-button"
                className="inline-flex items-center gap-2 bg-foreground text-background px-8 py-3 text-sm font-medium hover:bg-[#B8997A] hover:text-[#0B0F14] transition-colors disabled:opacity-60"
              >
                {uploading && <Loader2 size={14} className="animate-spin" />}
                Upload
              </button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
